import { useTranslation } from "@i18n";
import { convertDate } from "@Services/luxonHelper";

import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import type { Creator } from "@ServerTypes/reviewflows/reviewflows.schema";

/**
 * Every field mirrors the matching `reviewDataResultSchema` field on the review document
 * (`ReviewProperties` spreads them off `state.reviewData`), so they carry that schema's optionality.
 */
interface ReviewBasicDetailsProps {
  readonly reviewFlowName?: string;
  readonly createdBy?: Creator;
  readonly currentReviewLevelSerial?: number;
  readonly currentReviewLevelTitle?: string;
  readonly nextActionDueDate?: number | null;
  readonly createdDate?: number;
  readonly finishedDate?: number | null;
  readonly numberOfEntitiesAttached?: number;
}

function ReviewBasicDetails({
  reviewFlowName,
  createdBy,
  currentReviewLevelSerial,
  currentReviewLevelTitle,
  nextActionDueDate,
  createdDate,
  finishedDate,
  numberOfEntitiesAttached,
}: ReviewBasicDetailsProps) {
  const { t } = useTranslation();
  const getFormattedDate = (date?: number | null) => {
    if (date) {
      return convertDate(Number(date), "dateForText");
    }

    return "-";
  };

  const getUserAvatarComponent = (user: Creator) => <UserNameTagComponent id={user.userId} />;

  return (
    <div className="pb-6 ps-4">
      <div className="flex flex-row items-center justify-between py-3">
        <span className="text-base font-semibold">{t("Review Flow Name")}</span>
        <div className="w-[60%] self-start">
          <span>{reviewFlowName}</span>
        </div>
      </div>

      <div className="flex flex-row items-center justify-between py-3">
        <span className="text-base font-semibold">{t("Created by")}</span>
        <div className="w-[60%] self-start">
          <span
            className="text-base font-semibold flex-1 overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
            data-testid="userProfileDetail"
          >
            {/* KNOWN BUG, preserved verbatim: `createdBy` is optional on reviewDataResultSchema but is
                dereferenced (`user.userId`) unconditionally, so a review document without `createdBy`
                throws here. Does the server read it? N/A — read path only; the server writes
                `createdBy` on every createReview, so it is optional in schema but always present in
                practice. Fix: make `createdBy` required in reviewDataResultSchema, or render "-". */}
            {getUserAvatarComponent(createdBy!)}
          </span>
        </div>
      </div>

      <div className="flex flex-row items-center justify-between py-3">
        <span className="text-base font-semibold">{t("Waiting For")}</span>
        <div className="w-[60%] self-start">
          <span>
            {currentReviewLevelSerial && currentReviewLevelTitle
              ? `${currentReviewLevelSerial} - ${currentReviewLevelTitle}`
              : "-"}
          </span>
        </div>
      </div>

      <div className="flex flex-row items-center justify-between py-3">
        <span className="text-base font-semibold">{t("Next Action Due")}</span>
        <div className="w-[60%] self-start">
          <span>{getFormattedDate(nextActionDueDate)}</span>
        </div>
      </div>

      <div className="flex flex-row items-center justify-between py-3">
        <span className="text-base font-semibold">{t("Created On")}</span>
        <div className="w-[60%] self-start">
          <span>{getFormattedDate(createdDate)}</span>
        </div>
      </div>

      <div className="flex flex-row items-center justify-between py-3">
        <span className="text-base font-semibold">{t("Finished On")}</span>
        <div className="w-[60%] self-start">
          <span>{getFormattedDate(finishedDate)}</span>
        </div>
      </div>

      <div className="flex flex-row items-center justify-between py-3">
        <span className="text-base font-semibold">{t("Contents")}</span>
        <div className="w-[60%] self-start">
          <span>{numberOfEntitiesAttached}</span>
        </div>
      </div>
    </div>
  );
}

export default ReviewBasicDetails;
